import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, Plus, RefreshCw, X, Bell, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, Menu, Pause, Play, Rocket, ChevronRight, ChevronLeft, Tag, Download, Upload, Search, FileText } from 'lucide-react';

// Import utilities and hooks
import { API_URL } from './utils/api';
import { createDisplayArray } from './utils/tableHelpers';
import { useSSL } from './hooks/useSSL';
import { useDeployments } from './hooks/useDeployments';
import { useNotifications } from './hooks/useNotifications';
import { useAuth } from './contexts/AuthContext';

// Import components
import DeploymentManager from './components/DeploymentManager';
import NotificationManager from './components/NotificationManager';
import GroupManager from './components/GroupManager';
import AdsTxtManager from './components/AdsTxtManager';
import Modals from './components/Modals';
import LogoutButton from './components/LogoutButton';
import CertificateTableRow from './components/CertificateTableRow';
import EmptyTableRow from './components/EmptyTableRow';

// Layout Configuration
const LAYOUT_CONFIG = {
  sidebar: {
    width: {
      open: 'w-full md:w-64',
      closed: 'w-0 overflow-hidden'
    },
    position: 'md:fixed md:left-0 md:top-0 md:h-screen md:overflow-y-auto',
    background: 'bg-gray-900',
    border: 'border-r border-gray-800',
    transition: 'transition-all duration-300'
  },
  mainContent: {
    marginLeft: {
      open: 'md:ml-64',
      closed: 'md:ml-0'
    },
    transition: 'transition-all duration-300'
  },
  container: {
    background: 'bg-gray-950',
    textColor: 'text-white',
    layout: 'min-h-screen flex flex-col md:flex-row'
  }
};

// Active Filter Configuration
const ACTIVE_FILTER_CONFIG = {
  label: 'Active:',
  options: [
    { value: 'all', label: 'All' },
    { value: 'running', label: 'Running' },
    { value: 'paused', label: 'Paused' }
  ],
  default: 'all'
};

// Stats Cards Configuration
const STATS_CARDS_CONFIG = [
  {
    key: 'valid',
    label: 'Valid Certificates',
    filterValue: 'valid',
    gradient: 'from-emerald-900/40 to-emerald-800/20',
    colors: {
      border: 'border-emerald-700/30',
      borderActive: 'border-emerald-500',
      ring: 'ring-emerald-500/50',
      borderHover: 'hover:border-emerald-600/50',
      text: 'text-emerald-300',
      icon: 'text-emerald-400',
      iconBg: 'bg-emerald-500/20'
    },
    icon: Shield
  },
  {
    key: 'expiringSoon',
    label: 'Expiring Soon',
    filterValue: 'warning',
    gradient: 'from-yellow-900/40 to-yellow-800/20',
    colors: {
      border: 'border-yellow-700/30',
      borderActive: 'border-yellow-500',
      ring: 'ring-yellow-500/50',
      borderHover: 'hover:border-yellow-600/50',
      text: 'text-yellow-300',
      icon: 'text-yellow-400',
      iconBg: 'bg-yellow-500/20'
    },
    icon: Shield
  },
  {
    key: 'expired',
    label: 'Expired',
    filterValue: 'expired',
    gradient: 'from-red-900/40 to-red-800/20',
    colors: {
      border: 'border-red-700/30',
      borderActive: 'border-red-500',
      ring: 'ring-red-500/50',
      borderHover: 'hover:border-red-600/50',
      text: 'text-red-300',
      icon: 'text-red-400',
      iconBg: 'bg-red-500/20'
    },
    icon: Shield
  }
];

// Navigation Menu Configuration
const NAV_MENU_CONFIG = {
  items: [
    { 
      id: 'ssl', 
      label: 'SSL & Domain', 
      icon: Shield,
      description: 'SSL & Domain Management'
    },
    { 
      id: 'notifications', 
      label: 'Notifications', 
      icon: Bell,
      description: 'Notification Settings'
    },
    { 
      id: 'groups', 
      label: 'Groups', 
      icon: Tag,
      description: 'Group Management'
    },
    { 
      id: 'deployment', 
      label: 'Deployment', 
      icon: Rocket,
      description: 'Deployment History'
    },
    { 
      id: 'adstxt', 
      label: 'Ads.txt', 
      icon: FileText,
      description: 'Ads.txt Management'
    }
  ],
  styles: {
    active: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    inactive: 'text-gray-400 hover:bg-gray-800 hover:text-white',
    base: 'w-full flex items-center gap-3 px-3 py-2 md:px-4 md:py-3 rounded-lg transition-colors text-sm md:text-base'
  }
};

function SSLMonitorDashboard() {
  // Get auth headers for direct API calls in this component
  const { getAuthHeaders } = useAuth();
  
  // Use custom hooks for data management
  const {
    websites,
    pausedCertificates,
    setPausedCertificates,
    stats,
    fetchCertificates,
    fetchStatistics
  } = useSSL();

  const [notificationPausedCertificates, setNotificationPausedCertificates] = useState([]);
  const hasLoadedPausedStates = useRef(false);

  const {
    notifications,
    fetchNotifications
  } = useNotifications();

  const {
    products,
    setProducts,
    deploymentStats,
    fetchDeployments,
    fetchDeploymentStats
  } = useDeployments();

  // Local UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState({
    domain: '',
    warning_threshold: 30,
    expiry_threshold: 7,
    check_interval: 60,
    notification_channels: []
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isChecking, setIsChecking] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'days_left', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState('all');
  // Persist current page in localStorage to maintain state after refresh
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('ssl_monitor_current_page') || 'ssl';
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [newNotification, setNewNotification] = useState({
    name: '',
    type: 'discord',
    webhook_url: '',
    bot_name: 'SSL Monitor',
    prefix_message: '',
    default_enabled: true,
    resend_times: 2
  });
  const [isSavingNotification, setIsSavingNotification] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showCertificateDetails, setShowCertificateDetails] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'success' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [showAddNotificationModal, setShowAddNotificationModal] = useState(false);
  const [showEditNotificationModal, setShowEditNotificationModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [certificateSearchQuery, setCertificateSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(ACTIVE_FILTER_CONFIG.default);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [editingDeploymentType, setEditingDeploymentType] = useState(null); // { id, currentType }
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    id: '',
    name: '',
    description: '',
    color: 'blue',
    version_url: '',
    group_id: null
  });
  const [branchFilter, setBranchFilter] = useState('all');
  const [branchNameFilter, setBranchNameFilter] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [groups, setGroups] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('merge'); // 'merge' or 'replace'
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showBulkNotificationModal, setShowBulkNotificationModal] = useState(false);
  const [bulkNotificationId, setBulkNotificationId] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneProduct, setCloneProduct] = useState(null);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('ssl_monitor_current_page', currentPage);
  }, [currentPage]);

  // Fetch certificates on mount
  useEffect(() => {
    if (currentPage === 'ssl') {
      fetchCertificates();
      fetchStatistics();
      // Fetch notifications to display in the table
      if (notifications.length === 0) {
        fetchNotifications();
      }
    }
  }, [currentPage, fetchCertificates, fetchStatistics, fetchNotifications, notifications.length]);

  // Load notification paused states when websites data changes
  useEffect(() => {
    if (websites.length > 0 && !hasLoadedPausedStates.current) {
      const paused = websites
        .filter(site => site.notification_paused === 1)
        .map(site => site.id);
      setNotificationPausedCertificates(paused);
      hasLoadedPausedStates.current = true;
    }
  }, [websites]);

  // Define fetchGroups before it's used in useEffect
  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/groups`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      } else {
        console.error('Failed to fetch groups:', response.status);
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (currentPage === 'notifications') {
      fetchNotifications();
    }
  }, [currentPage, fetchNotifications]);

  useEffect(() => {
    if (currentPage === 'groups') {
      fetchGroups();
    }
  }, [currentPage, fetchGroups]);

  useEffect(() => {
    if (currentPage === 'deployment') {
      fetchDeployments();
      fetchDeploymentStats();
      fetchGroups();
      // Reset selectedProduct to show product list first
      setSelectedProduct(null);
      setBranchFilter('all');
      setBranchNameFilter([]);
      setProductSearchQuery('');
      setGroupFilter('all');
    }
  }, [currentPage, fetchDeployments, fetchDeploymentStats, fetchGroups]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu !== null) {
        // Check if click is outside the action menu and its trigger button
        const actionMenus = document.querySelectorAll('[data-action-menu]');
        const actionButtons = document.querySelectorAll('[data-action-button]');
        
        let clickedInside = false;
        
        // Check if clicked on any action button or menu
        actionMenus.forEach(menu => {
          if (menu.contains(event.target)) clickedInside = true;
        });
        
        actionButtons.forEach(button => {
          if (button.contains(event.target)) clickedInside = true;
        });
        
        if (!clickedInside) {
          setShowActionMenu(null);
        }
      }
    };

    if (showActionMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActionMenu]);

  const saveProduct = async () => {
    // Validate Product ID format (should contain name-number)
    if (!newProduct.id || !newProduct.id.trim()) {
      setAlertModal({ show: true, message: 'Product ID is required (e.g., MyApp-1)', type: 'error' });
      return;
    }

    if (!newProduct.name || !newProduct.name.trim()) {
      setAlertModal({ show: true, message: 'Product name is required', type: 'error' });
      return;
    }

    if (!newProduct.group_id) {
      setAlertModal({ show: true, message: 'Please select a group', type: 'error' });
      return;
    }

    // Validate ID format: should contain hyphen and have text on both sides
    const idPattern = /^[a-zA-Z0-9]+-[a-zA-Z0-9]+$/;
    if (!idPattern.test(newProduct.id.trim())) {
      setAlertModal({ show: true, message: 'Product ID must be in format: name-number (e.g., MyApp-1, QuizApp-2)', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newProduct)
      });

      if (response.ok) {
        // Reset form
        setNewProduct({
          id: '',
          name: '',
          description: '',
          color: 'blue',
          version_url: '',
          group_id: null
        });
        setShowAddProductModal(false);
        // Refresh products
        fetchDeployments();
        setAlertModal({ show: true, message: 'Product added successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to add product', type: 'error' });
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setAlertModal({ show: true, message: 'Failed to save product', type: 'error' });
    }
  };

  const deleteProduct = async (productId, productName) => {
    // Show custom confirmation modal
    setProductToDelete({ id: productId, name: productName });
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const response = await fetch(`${API_URL}/products/${productToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        // Close modal and reset
        setShowDeleteConfirmModal(false);
        setProductToDelete(null);
        // Refresh products list
        fetchDeployments();
        setAlertModal({ show: true, message: 'Product deleted successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to delete product', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      setAlertModal({ show: true, message: 'Failed to delete product', type: 'error' });
    }
  };

  // Export products handler
  const handleExportProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products/export/all`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `products-export-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setAlertModal({ show: true, message: 'Products exported successfully!', type: 'success' });
      } else {
        setAlertModal({ show: true, message: 'Failed to export products', type: 'error' });
      }
    } catch (error) {
      console.error('Error exporting products:', error);
      setAlertModal({ show: true, message: 'Failed to export products', type: 'error' });
    }
  };

  // Import products handler
  const handleImportProducts = () => {
    setShowImportModal(true);
    setImportMode('merge');
    setImportFile(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/json') {
        setAlertModal({ show: true, message: 'Please select a valid JSON file', type: 'error' });
        return;
      }
      setImportFile(file);
    }
  };

  const confirmImportProducts = async () => {
    if (!importFile) {
      setAlertModal({ show: true, message: 'Please select a file to import', type: 'error' });
      return;
    }

    try {
      const fileContent = await importFile.text();
      const importData = JSON.parse(fileContent);

      const response = await fetch(`${API_URL}/products/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          data: importData,
          mode: importMode
        })
      });

      if (response.ok) {
        const result = await response.json();
        setShowImportModal(false);
        setImportFile(null);
        fetchDeployments();
        fetchGroups();
        
        let message = `Import completed! Products: ${result.stats.products}, Builds: ${result.stats.builds}`;
        if (result.stats.groups > 0) {
          message += `, Groups: ${result.stats.groups}`;
        }
        if (result.stats.skipped > 0) {
          message += `, Skipped: ${result.stats.skipped}`;
        }
        
        setAlertModal({ show: true, message, type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to import products', type: 'error' });
      }
    } catch (error) {
      console.error('Error importing products:', error);
      setAlertModal({ show: true, message: 'Failed to import products. Please check the file format.', type: 'error' });
    }
  };

  const handleCloneProduct = async (product) => {
    // Generate new ID by incrementing the number
    const match = product.id.match(/^(.+)-(\d+)$/);
    let newId = `${product.id}-copy`;
    
    if (match) {
      const prefix = match[1];
      const number = parseInt(match[2]);
      // Find highest number with this prefix
      const maxNumber = products
        .filter(p => p.id.startsWith(prefix + '-'))
        .map(p => {
          const m = p.id.match(/^.+-(\d+)$/);
          return m ? parseInt(m[1]) : 0;
        })
        .reduce((max, num) => Math.max(max, num), number);
      
      newId = `${prefix}-${maxNumber + 1}`;
    }

    // Set up clone data and show modal
    setCloneProduct({
      id: newId,
      name: `${product.name} (Copy)`,
      description: product.description || '',
      color: product.color || 'blue',
      version_url: product.version_url || '',
      group_id: product.group_id
    });
    setShowCloneModal(true);
  };

  const confirmCloneProduct = async () => {
    if (!cloneProduct || !cloneProduct.id || !cloneProduct.id.trim()) {
      setAlertModal({ show: true, message: 'Product ID is required', type: 'error' });
      return;
    }

    if (!cloneProduct.name || !cloneProduct.name.trim()) {
      setAlertModal({ show: true, message: 'Product name is required', type: 'error' });
      return;
    }

    if (!cloneProduct.group_id) {
      setAlertModal({ show: true, message: 'Please select a group', type: 'error' });
      return;
    }

    // Validate ID format: should contain hyphen and have text on both sides
    const idPattern = /^[a-zA-Z0-9]+-[a-zA-Z0-9]+$/;
    if (!idPattern.test(cloneProduct.id.trim())) {
      setAlertModal({ show: true, message: 'Product ID must be in format: name-number (e.g., MyApp-1, QuizApp-2)', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cloneProduct)
      });

      if (response.ok) {
        setShowCloneModal(false);
        setCloneProduct(null);
        fetchDeployments();
        setAlertModal({ show: true, message: `Product cloned successfully as "${cloneProduct.id}"!`, type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to clone product', type: 'error' });
      }
    } catch (error) {
      console.error('Error cloning product:', error);
      setAlertModal({ show: true, message: 'Failed to clone product', type: 'error' });
    }
  };

  const editProduct = (product) => {
    // Fetch groups before opening modal
    fetchGroups();
    setEditingProduct({
      id: product.id,
      name: product.name,
      description: product.description || '',
      color: product.color || 'blue',
      version_url: product.version_url || '',
      group_id: product.group_id || null
    });
    setShowEditProductModal(true);
  };

  const updateProduct = async () => {
    if (!editingProduct || !editingProduct.name || !editingProduct.name.trim()) {
      setAlertModal({ show: true, message: 'Product name is required', type: 'error' });
      return;
    }

    if (!editingProduct.group_id) {
      setAlertModal({ show: true, message: 'Please select a group', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: editingProduct.name,
          description: editingProduct.description,
          color: editingProduct.color,
          version_url: editingProduct.version_url,
          group_id: editingProduct.group_id
        })
      });

      if (response.ok) {
        setShowEditProductModal(false);
        setEditingProduct(null);
        // Refresh products list
        fetchDeployments();
        setAlertModal({ show: true, message: 'Product updated successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to update product', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating product:', error);
      setAlertModal({ show: true, message: 'Failed to update product', type: 'error' });
    }
  };

  const updateDeploymentType = async (buildId, newType) => {
    // Find the product containing this deployment
    const currentProduct = products.find(p => 
      p.deployments.some(d => d.id === buildId)
    );

    if (!currentProduct) return;

    // Optimistic UI update with cascading logic
    setProducts(prevProducts => 
      prevProducts.map(product => {
        if (product.id !== currentProduct.id) return product;
        
        return {
          ...product,
          deployments: product.deployments.map(deployment => {
            // Current deployment being updated
            if (deployment.id === buildId) {
              return { ...deployment, deployment_type: newType };
            }
            
            // Cascade logic for other deployments
            if (newType === 'live') {
              // If setting new LIVE, move old LIVE to PREVIOUS and clear old PREVIOUS
              if (deployment.deployment_type === 'live') {
                return { ...deployment, deployment_type: 'previous' };
              }
              if (deployment.deployment_type === 'previous') {
                return { ...deployment, deployment_type: null };
              }
            } else if (newType === 'previous') {
              // If setting new PREVIOUS, clear old PREVIOUS
              if (deployment.deployment_type === 'previous') {
                return { ...deployment, deployment_type: null };
              }
            }
            
            return deployment;
          })
        };
      })
    );
    
    // Close editing mode immediately
    setEditingDeploymentType(null);

    try {
      // Collect all updates needed (cascading changes)
      const updates = [];
      
      if (newType === 'live') {
        // Find current LIVE and PREVIOUS deployments
        const currentLive = currentProduct.deployments.find(d => d.deployment_type === 'live');
        const currentPrevious = currentProduct.deployments.find(d => d.deployment_type === 'previous');
        
        if (currentLive) {
          updates.push({ id: currentLive.id, type: 'previous' });
        }
        if (currentPrevious) {
          updates.push({ id: currentPrevious.id, type: null });
        }
      } else if (newType === 'previous') {
        // Find current PREVIOUS deployment
        const currentPrevious = currentProduct.deployments.find(d => d.deployment_type === 'previous');
        
        if (currentPrevious) {
          updates.push({ id: currentPrevious.id, type: null });
        }
      }
      
      // Add the main update
      updates.push({ id: buildId, type: newType });
      
      // Execute all updates
      const updatePromises = updates.map(update => 
        fetch(`${API_URL}/builds/${update.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ deployment_type: update.type })
        })
      );
      
      const responses = await Promise.all(updatePromises);
      
      if (responses.every(r => r.ok)) {
        // Refresh deployments in background to sync with server
        fetchDeployments();
      } else {
        console.error('Failed to update deployment type');
        // Revert optimistic update on failure
        fetchDeployments();
        setAlertModal({ show: true, message: 'Failed to update deployment type', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating deployment type:', error);
      // Revert optimistic update on error
      fetchDeployments();
      setAlertModal({ show: true, message: 'Failed to update deployment type', type: 'error' });
    }
  };

  const saveNotification = async () => {
    if (!newNotification.name || !newNotification.webhook_url) {
      setAlertModal({ show: true, message: 'Please enter a friendly name and webhook URL', type: 'error' });
      return;
    }

    setIsSavingNotification(true);
    try {
      const response = await fetch(`${API_URL}/notifications`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newNotification),
      });

      if (response.ok) {
        await fetchNotifications();
        // Reset form
        setNewNotification({
          name: '',
          type: 'discord',
          webhook_url: '',
          bot_name: 'SSL Monitor',
          prefix_message: '',
          default_enabled: true
        });
        setAlertModal({ show: true, message: 'Notification saved successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: `Error: ${error.error}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error saving notification:', error);
      setAlertModal({ show: true, message: 'Failed to save notification', type: 'error' });
    } finally {
      setIsSavingNotification(false);
    }
  };

  const testNotification = async () => {
    if (!newNotification.webhook_url) {
      setAlertModal({ show: true, message: 'Please enter a webhook URL first', type: 'error' });
      return;
    }

    // Create temporary notification for testing
    try {
      const tempNotification = await fetch(`${API_URL}/notifications`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newNotification,
          name: newNotification.name || 'Test Notification'
        }),
      });

      if (tempNotification.ok) {
        const data = await tempNotification.json();
        const testResponse = await fetch(`${API_URL}/notifications/${data.id}/test`, {
          method: 'POST',
          headers: getAuthHeaders()
        });

        if (testResponse.ok) {
          setAlertModal({ show: true, message: 'Test notification sent! Check your Discord channel.', type: 'success' });
          // Delete the temporary notification
          await fetch(`${API_URL}/notifications/${data.id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
          });
        } else {
          const error = await testResponse.json();
          setAlertModal({ show: true, message: `Test failed: ${error.error}`, type: 'error' });
        }
      }
    } catch (error) {
      console.error('Error testing notification:', error);
      setAlertModal({ show: true, message: 'Failed to send test notification', type: 'error' });
    }
  };

  const deleteNotification = async (id) => {
    setConfirmAction({
      title: 'Delete Notification',
      message: 'Are you sure you want to delete this notification?',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/notifications/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
          });

          if (response.ok) {
            await fetchNotifications();
            setAlertModal({ show: true, message: 'Notification deleted successfully', type: 'success' });
          }
        } catch (error) {
          console.error('Error deleting notification:', error);
          setAlertModal({ show: true, message: 'Failed to delete notification', type: 'error' });
        }
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const updateNotification = async () => {
    if (!editingNotification || !editingNotification.name || !editingNotification.webhook_url) {
      setAlertModal({ show: true, message: 'Please enter a friendly name and webhook URL', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/notifications/${editingNotification.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editingNotification),
      });

      if (response.ok) {
        await fetchNotifications();
        setShowEditNotificationModal(false);
        setEditingNotification(null);
        setAlertModal({ show: true, message: 'Notification updated successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: `Error: ${error.error}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error updating notification:', error);
      setAlertModal({ show: true, message: 'Failed to update notification', type: 'error' });
    }
  };

  const applyNotificationToAll = async (notificationId) => {
    setConfirmAction({
      title: 'Apply Notification to All Certificates',
      message: 'This will assign this notification to ALL certificates. Continue?',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/notifications/${notificationId}/apply-to-all`, {
            method: 'POST',
            headers: getAuthHeaders()
          });

          if (response.ok) {
            const result = await response.json();
            await fetchCertificates();
            setAlertModal({ 
              show: true, 
              message: `Successfully applied notification to ${result.updated} certificate(s)!`, 
              type: 'success' 
            });
          } else {
            const error = await response.json();
            setAlertModal({ show: true, message: error.error || 'Failed to apply notification', type: 'error' });
          }
        } catch (error) {
          console.error('Error applying notification:', error);
          setAlertModal({ show: true, message: 'Failed to apply notification to all certificates', type: 'error' });
        }
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const updateCertificate = async () => {
    if (!editingCertificate) return;

    try {
      const response = await fetch(`${API_URL}/certificates/${editingCertificate.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          warning_threshold: editingCertificate.warning_threshold,
          expiry_threshold: editingCertificate.expiry_threshold,
          check_interval: editingCertificate.check_interval,
          notification_id: editingCertificate.notification_id || null
        }),
      });

      if (response.ok) {
        await fetchCertificates();
        await fetchStatistics();
        setShowEditModal(false);
        setEditingCertificate(null);
        setAlertModal({ show: true, message: 'Certificate updated successfully!', type: 'success' });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: `Error: ${error.error}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error updating certificate:', error);
      setAlertModal({ show: true, message: 'Failed to update certificate', type: 'error' });
    }
  };

  const addDomain = async () => {
    if (!newDomain.domain.trim()) return;

    setIsChecking(true);
    try {
      const response = await fetch(`${API_URL}/certificates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newDomain),
      });

      if (response.ok) {
        await fetchCertificates();
        await fetchStatistics();
        setNewDomain({
          domain: '',
          warning_threshold: 30,
          expiry_threshold: 7,
          check_interval: 1,
          notification_channels: []
        });
        setShowAddModal(false);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      alert('Failed to add domain');
    } finally {
      setIsChecking(false);
    }
  };

  const removeDomain = async (id) => {
    setShowActionMenu(null);
    setConfirmAction({
      title: 'Delete Certificate',
      message: 'Are you sure you want to delete this certificate?',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/certificates/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
          });

          if (response.ok) {
            await fetchCertificates();
            await fetchStatistics();
            setAlertModal({ show: true, message: 'Certificate deleted successfully', type: 'success' });
          }
        } catch (error) {
          console.error('Error deleting certificate:', error);
          setAlertModal({ show: true, message: 'Failed to delete certificate', type: 'error' });
        }
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const refreshAll = async () => {
    try {
      // Call backend to re-check all SSL certificates
      await fetch(`${API_URL}/certificates/refresh-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      // Fetch updated data
      await fetchCertificates();
      await fetchStatistics();
    } catch (error) {
      console.error('Error refreshing all certificates:', error);
    }
  };

  const refreshSingle = async (id) => {
    try {
      await fetch(`${API_URL}/certificates/${id}/refresh`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      await fetchCertificates();
      await fetchStatistics();
    } catch (error) {
      console.error('Error refreshing certificate:', error);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredWebsites = () => {
    let filtered = [...websites];

    // Apply search filter
    if (certificateSearchQuery) {
      const searchLower = certificateSearchQuery.toLowerCase();
      filtered = filtered.filter(site => 
        site.domain.toLowerCase().includes(searchLower) ||
        (site.issuer && site.issuer.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'warning') {
        // Include both 'warning' and 'expiring' statuses for "Expiring Soon" filter
        filtered = filtered.filter(site => site.status === 'warning' || site.status === 'expiring');
      } else {
        filtered = filtered.filter(site => site.status === statusFilter);
      }
    }

    // Apply notification filter
    if (activeFilter !== ACTIVE_FILTER_CONFIG.default) {
      if (activeFilter === 'paused') {
        filtered = filtered.filter(site => pausedCertificates.includes(site.id));
      } else if (activeFilter === 'running') {
        filtered = filtered.filter(site => !pausedCertificates.includes(site.id));
      }
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle numeric sorting for days_left
        if (sortConfig.key === 'days_left') {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 text-gray-500" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-blue-400" /> : 
      <ArrowDown className="w-4 h-4 text-blue-400" />;
  };

  // Multi-select functions
  const toggleSelectAll = () => {
    if (selectedCertificates.length === filteredWebsites.length) {
      setSelectedCertificates([]);
    } else {
      setSelectedCertificates(filteredWebsites.map(site => site.id));
    }
  };

  const toggleSelectCertificate = (id) => {
    if (selectedCertificates.includes(id)) {
      setSelectedCertificates(selectedCertificates.filter(certId => certId !== id));
    } else {
      setSelectedCertificates([...selectedCertificates, id]);
    }
  };

  // Pause/Resume functions
  const togglePause = async (id) => {
    const isPaused = pausedCertificates.includes(id);
    const newPausedState = !isPaused;
    
    try {
      await fetch(`${API_URL}/certificates/${id}/pause`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_paused: newPausedState })
      });
      
      if (newPausedState) {
        setPausedCertificates([...pausedCertificates, id]);
      } else {
        setPausedCertificates(pausedCertificates.filter(certId => certId !== id));
      }
    } catch (error) {
      console.error('Error toggling pause:', error);
    }
  };

  // Toggle action menu - close if already open, open if closed
  const handleToggleActionMenu = (siteId) => {
    setShowActionMenu(prevMenu => prevMenu === siteId ? null : siteId);
  };

  // Toggle notification pause
  const toggleNotificationPause = async (id) => {
    const isNotificationPaused = notificationPausedCertificates.includes(id);
    const newState = !isNotificationPaused;
    
    try {
      await fetch(`${API_URL}/certificates/${id}/pause-notification`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notification_paused: newState })
      });
      
      // Update local state immediately
      if (newState) {
        setNotificationPausedCertificates([...notificationPausedCertificates, id]);
      } else {
        setNotificationPausedCertificates(notificationPausedCertificates.filter(certId => certId !== id));
      }
    } catch (error) {
      console.error('Error toggling notification pause:', error);
    }
  };

  // Bulk pause/resume
  const bulkPause = async () => {
    try {
      await fetch(`${API_URL}/certificates/bulk-pause`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ids: selectedCertificates, is_paused: true })
      });
      
      const newPaused = [...pausedCertificates];
      selectedCertificates.forEach(id => {
        if (!newPaused.includes(id)) {
          newPaused.push(id);
        }
      });
      setPausedCertificates(newPaused);
    } catch (error) {
      console.error('Error bulk pausing:', error);
    }
  };

  const bulkResume = async () => {
    try {
      await fetch(`${API_URL}/certificates/bulk-pause`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ids: selectedCertificates, is_paused: false })
      });
      
      const newPaused = pausedCertificates.filter(id => !selectedCertificates.includes(id));
      setPausedCertificates(newPaused);
    } catch (error) {
      console.error('Error bulk resuming:', error);
    }
  };

  const applyBulkNotification = async () => {
    if (!bulkNotificationId) {
      setAlertModal({ show: true, message: 'Please select a notification', type: 'error' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/certificates/bulk-notification`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          ids: selectedCertificates, 
          notification_id: bulkNotificationId === 'none' ? null : parseInt(bulkNotificationId)
        })
      });

      if (response.ok) {
        await fetchCertificates();
        setShowBulkNotificationModal(false);
        setBulkNotificationId('');
        setSelectedCertificates([]);
        setAlertModal({ 
          show: true, 
          message: `Notification ${bulkNotificationId === 'none' ? 'removed from' : 'applied to'} ${selectedCertificates.length} certificate(s)!`, 
          type: 'success' 
        });
      } else {
        const error = await response.json();
        setAlertModal({ show: true, message: error.error || 'Failed to apply notification', type: 'error' });
      }
    } catch (error) {
      console.error('Error applying bulk notification:', error);
      setAlertModal({ show: true, message: 'Failed to apply notification', type: 'error' });
    }
  };

  // Export certificates to CSV
  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API_URL}/certificates/export/csv`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ssl-certificates-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setAlertModal({ show: true, message: 'Certificates exported successfully!', type: 'success' });
    } catch (error) {
      console.error('Error exporting:', error);
      setAlertModal({ show: true, message: 'Failed to export certificates', type: 'error' });
    }
  };

  // Import certificates from CSV
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      
      const response = await fetch(`${API_URL}/certificates/import/csv`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ csvData: text })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setAlertModal({ 
          show: true, 
          message: `Import completed! Imported: ${result.imported}, Skipped: ${result.skipped}`,
          type: 'success' 
        });
        fetchCertificates();
        fetchStatistics();
      } else {
        setAlertModal({ show: true, message: result.error || 'Import failed', type: 'error' });
      }
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Error importing:', error);
      setAlertModal({ show: true, message: 'Failed to import certificates', type: 'error' });
    }
  };

  const filteredWebsites = getSortedAndFilteredWebsites();

  // Calculate pagination
  const totalPages = Math.ceil(filteredWebsites.length / rowsPerPage);
  const startIndex = (pageNumber - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedWebsites = filteredWebsites.slice(startIndex, endIndex);

  // Reset to page 1 when filters or rowsPerPage change
  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, rowsPerPage, certificateSearchQuery, activeFilter]);

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const renderSSLPage = () => {
    return (
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold mb-1">SSL & Domain Management</h1>
              <p className="text-xs md:text-sm text-gray-400">Monitor and manage SSL certificates for your domains</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-sm"
                title="Export to CSV"
              >
                <Download className="w-4 h-4" />
                <span className="hidden md:inline">Export</span>
              </button>
              <label className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-sm cursor-pointer"
                title="Import from CSV"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden md:inline">Import</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="hidden"
                />
              </label>
              <button
                onClick={refreshAll}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh All</span>
                <span className="sm:hidden">Refresh</span>
              </button>
              <button
                onClick={() => {
                  setShowAddModal(true);
                  // Fetch notifications if not already loaded
                  if (notifications.length === 0) {
                    fetchNotifications();
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Domain</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-4 flex flex-wrap gap-3">
            {/* Search Box */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search domains or issuers..."
                value={certificateSearchQuery}
                onChange={(e) => setCertificateSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm placeholder-gray-500 w-full"
              />
              {certificateSearchQuery && (
                <button
                  onClick={() => setCertificateSearchQuery('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Active Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400 whitespace-nowrap">{ACTIVE_FILTER_CONFIG.label}</label>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
              >
                {ACTIVE_FILTER_CONFIG.options.map((option) => {
                  let count = websites.length;
                  if (option.value === 'running') {
                    count = websites.filter(w => !pausedCertificates.includes(w.id)).length;
                  } else if (option.value === 'paused') {
                    count = pausedCertificates.length;
                  }
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label} {option.value !== 'all' ? `(${count})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Clear All Filters */}
            {(certificateSearchQuery || activeFilter !== ACTIVE_FILTER_CONFIG.default) && (
              <button
                onClick={() => {
                  setCertificateSearchQuery('');
                  setActiveFilter(ACTIVE_FILTER_CONFIG.default);
                }}
                className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Clear Filters
              </button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {STATS_CARDS_CONFIG.map((card) => {
              const IconComponent = card.icon;
              const isActive = statusFilter === card.filterValue;
              
              return (
                <button
                  key={card.key}
                  onClick={() => setStatusFilter(statusFilter === card.filterValue ? 'all' : card.filterValue)}
                  className={`bg-gradient-to-br ${card.gradient} p-3 rounded-lg border transition-all text-left ${
                    isActive ? `${card.colors.borderActive} ring-2 ${card.colors.ring}` : card.colors.border
                  } relative overflow-hidden ${card.colors.borderHover} cursor-pointer`}
                >
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 36px)', backgroundSize: '100% 100%'}}></div>
                  </div>
                  <div className="relative flex items-center justify-between">
                    <div>
                      <p className={`${card.colors.text} text-xs mb-1`}>{card.label}</p>
                      <p className="text-2xl md:text-3xl font-bold">{stats[card.key]}</p>
                    </div>
                    <div className={`w-10 h-10 md:w-12 md:h-12 ${card.colors.iconBg} rounded-full flex items-center justify-center`}>
                      <IconComponent className={`w-5 h-5 md:w-6 md:h-6 ${card.colors.icon}`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedCertificates.length > 0 && (
          <div className="mb-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 md:gap-3">
              <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
              <span className="text-xs md:text-sm text-blue-400 font-medium">
                {selectedCertificates.length} certificate{selectedCertificates.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={bulkPause}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors border border-yellow-500/30 text-xs md:text-sm font-medium"
              >
                <Pause className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Pause Selected</span>
                <span className="sm:hidden">Pause</span>
              </button>
              <button
                onClick={bulkResume}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors border border-emerald-500/30 text-xs md:text-sm font-medium"
              >
                <Play className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Resume Selected</span>
                <span className="sm:hidden">Resume</span>
              </button>
              <button
                onClick={() => setShowBulkNotificationModal(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors border border-purple-500/30 text-xs md:text-sm font-medium"
              >
                <Bell className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Set Notification</span>
                <span className="sm:hidden">Notify</span>
              </button>
              <button
                onClick={() => setSelectedCertificates([])}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 text-xs md:text-sm font-medium"
              >
                <X className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Clear Selection</span>
                <span className="sm:hidden">Clear</span>
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50 border-b border-gray-700">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-300 w-12">
                    <input
                      type="checkbox"
                      checked={selectedCertificates.length === filteredWebsites.length && filteredWebsites.length > 0}
                      onChange={toggleSelectAll}
                      className="w-3 h-3 md:w-4 md:h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-300">
                    <button 
                      onClick={() => handleSort('domain')} 
                      className="flex items-center gap-1 hover:text-white transition-colors"
                    >
                      Domain
                      {getSortIcon('domain')}
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-300">
                    <button 
                      onClick={() => handleSort('status')} 
                      className="flex items-center gap-1 hover:text-white transition-colors"
                    >
                      Status
                      {getSortIcon('status')}
                    </button>
                  </th>
                  <th className="hidden lg:table-cell px-3 py-2.5 text-left text-xs font-medium text-gray-300">
                    <button 
                      onClick={() => handleSort('issuer')} 
                      className="flex items-center gap-1 hover:text-white transition-colors"
                    >
                      Issuer
                      {getSortIcon('issuer')}
                    </button>
                  </th>
                  <th className="hidden md:table-cell px-3 py-2.5 text-left text-xs font-medium text-gray-300">
                    <button 
                      onClick={() => handleSort('valid_until')} 
                      className="flex items-center gap-1 hover:text-white transition-colors"
                    >
                      Valid Until
                      {getSortIcon('valid_until')}
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-300">
                    <button 
                      onClick={() => handleSort('days_left')} 
                      className="flex items-center gap-1 hover:text-white transition-colors"
                    >
                      Days Left
                      {getSortIcon('days_left')}
                    </button>
                  </th>
                  <th className="hidden xl:table-cell px-3 py-2.5 text-left text-xs font-medium text-gray-300">
                    <button 
                      onClick={() => handleSort('check_interval')} 
                      className="flex items-center gap-1 hover:text-white transition-colors"
                    >
                      Check Interval
                      {getSortIcon('check_interval')}
                    </button>
                  </th>
                  <th className="hidden lg:table-cell px-3 py-2.5 text-left text-xs font-medium text-gray-300">Notification</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {createDisplayArray(paginatedWebsites, rowsPerPage).map((site, index) => 
                  site ? (
                    <CertificateTableRow
                      key={site.id}
                      site={site}
                      isPaused={pausedCertificates.includes(site.id)}
                      isNotificationPaused={notificationPausedCertificates.includes(site.id)}
                      isSelected={selectedCertificates.includes(site.id)}
                      showActionMenu={showActionMenu}
                      notifications={notifications}
                      onToggleSelect={toggleSelectCertificate}
                      onViewDetails={(cert) => {
                        setSelectedCertificate(cert);
                        setShowCertificateDetails(true);
                      }}
                      onToggleActionMenu={handleToggleActionMenu}
                      onRefresh={refreshSingle}
                      onTogglePause={togglePause}
                      onToggleNotificationPause={toggleNotificationPause}
                      onEdit={(cert) => {
                        setEditingCertificate(cert);
                        setShowEditModal(true);
                        if (notifications.length === 0) {
                          fetchNotifications();
                        }
                      }}
                      onDelete={removeDomain}
                      formatDate={formatDate}
                    />
                  ) : (
                    <EmptyTableRow key={`empty-${index}`} index={index} />
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="px-3 py-2.5 border-t border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <span>Rows Per Page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs md:text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              {statusFilter !== 'all' && (
                <span className="ml-2 md:ml-4 px-2 md:px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs border border-blue-500/30 flex items-center gap-2">
                  <span className="hidden sm:inline">Filter:</span> {statusFilter === 'valid' ? 'Valid' : statusFilter === 'warning' ? 'Expiring Soon' : 'Expired'}
                  <button onClick={() => setStatusFilter('all')} className="hover:text-blue-300">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs md:text-sm">
                {filteredWebsites.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, filteredWebsites.length)} of ${filteredWebsites.length}` : '0 of 0'} certificates {statusFilter !== 'all' && `(${websites.length} total)`}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePrevPage}
                  disabled={pageNumber === 1}
                  className="p-1.5 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs md:text-sm min-w-[60px] text-center">
                  {totalPages > 0 ? pageNumber : 0}
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={pageNumber === totalPages || totalPages === 0}
                  className="p-1.5 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNotificationsPage = () => {
    return (
      <NotificationManager
        notifications={notifications}
        setShowAddNotificationModal={setShowAddNotificationModal}
        setShowEditNotificationModal={setShowEditNotificationModal}
        setEditingNotification={setEditingNotification}
        deleteNotification={deleteNotification}
        applyNotificationToAll={applyNotificationToAll}
      />
    );
  };

  const renderDeploymentPage = () => {
    return (
      <DeploymentManager
        products={products}
        deploymentStats={deploymentStats}
        selectedProduct={selectedProduct}
        branchFilter={branchFilter}
        branchNameFilter={branchNameFilter}
        productSearchQuery={productSearchQuery}
        groupFilter={groupFilter}
        editingDeploymentType={editingDeploymentType}
        groups={groups}
        setSelectedProduct={setSelectedProduct}
        setBranchFilter={setBranchFilter}
        setBranchNameFilter={setBranchNameFilter}
        setProductSearchQuery={setProductSearchQuery}
        setGroupFilter={setGroupFilter}
        setEditingDeploymentType={setEditingDeploymentType}
        setSelectedDeployment={setSelectedDeployment}
        setShowAddProductModal={setShowAddProductModal}
        setShowEditProductModal={setShowEditProductModal}
        setEditingProduct={setEditingProduct}
        setShowDeleteConfirmModal={setShowDeleteConfirmModal}
        setProductToDelete={setProductToDelete}
        updateDeploymentType={updateDeploymentType}
        fetchDeployments={fetchDeployments}
        fetchDeploymentStats={fetchDeploymentStats}
        fetchGroups={fetchGroups}
        handleExportProducts={handleExportProducts}
        handleImportProducts={handleImportProducts}
        handleCloneProduct={handleCloneProduct}
      />
    );
  };

  const renderGroupsPage = () => {
    return (
      <GroupManager
        groups={groups}
        fetchGroups={fetchGroups}
        API_URL={API_URL}
        getAuthHeaders={getAuthHeaders}
      />
    );
  };

  const renderAdsTxtPage = () => {
    return <AdsTxtManager />;
  };

  // Utility functions for Modals component
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Ensure UTC timezone - PostgreSQL returns timestamps without 'Z'
    const utcDate = dateString.includes('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDate);
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = dayNames[date.getDay()];
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${dayName} ${day} ${month} ${year} at ${hours}:${minutes}:${seconds}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'expiring':
        return 'text-orange-400';
      case 'expired':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'expiring':
        return '🔥';
      case 'expired':
        return '⛔';
      default:
        return '❓';
    }
  };

  return (
    <div className={`${LAYOUT_CONFIG.container.layout} ${LAYOUT_CONFIG.container.background} ${LAYOUT_CONFIG.container.textColor}`}>
      {/* Sidebar - Fixed Position */}
      <div className={`${LAYOUT_CONFIG.sidebar.background} ${LAYOUT_CONFIG.sidebar.border} ${LAYOUT_CONFIG.sidebar.transition} ${LAYOUT_CONFIG.sidebar.position} ${
        sidebarOpen ? LAYOUT_CONFIG.sidebar.width.open : LAYOUT_CONFIG.sidebar.width.closed
      }`}>
        <div className="p-4 md:p-6">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <h2 className="text-lg md:text-xl font-bold">SSL Monitor</h2>
          </div>

          <nav className="space-y-2">
            {NAV_MENU_CONFIG.items.map((item) => {
              const IconComponent = item.icon;
              const isActive = currentPage === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => setCurrentPage(item.id)}
                  className={`${NAV_MENU_CONFIG.styles.base} ${
                    isActive ? NAV_MENU_CONFIG.styles.active : NAV_MENU_CONFIG.styles.inactive
                  }`}
                >
                  <IconComponent className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content - Margin for Fixed Sidebar */}
      <div className={`flex-1 overflow-auto ${LAYOUT_CONFIG.mainContent.transition} ${
        sidebarOpen ? LAYOUT_CONFIG.mainContent.marginLeft.open : LAYOUT_CONFIG.mainContent.marginLeft.closed
      }`}>
        {/* Top Bar */}
        <div className="bg-gray-900 border-b border-gray-800 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <Menu className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <div className="text-xs md:text-sm text-gray-400 truncate">
              {NAV_MENU_CONFIG.items.find(item => item.id === currentPage)?.description || 'Dashboard'}
            </div>
          </div>
          <LogoutButton />
        </div>

        {/* Page Content */}
        <div className="p-3">
          {currentPage === 'ssl' ? renderSSLPage() : 
           currentPage === 'notifications' ? renderNotificationsPage() : 
           currentPage === 'groups' ? renderGroupsPage() :
           currentPage === 'adstxt' ? renderAdsTxtPage() :
           renderDeploymentPage()}
        </div>
      </div>

      {/* All Modals */}
      <Modals
        // Certificate modals
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        newDomain={newDomain}
        setNewDomain={setNewDomain}
        notifications={notifications}
        isChecking={isChecking}
        addDomain={addDomain}
        
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        editingCertificate={editingCertificate}
        setEditingCertificate={setEditingCertificate}
        updateCertificate={updateCertificate}
        
        showCertificateDetails={showCertificateDetails}
        setShowCertificateDetails={setShowCertificateDetails}
        selectedCertificate={selectedCertificate}
        refreshSingle={refreshSingle}
        getStatusColor={getStatusColor}
        getStatusIcon={getStatusIcon}
        formatDate={formatDate}
        
        // Notification modals
        showAddNotificationModal={showAddNotificationModal}
        setShowAddNotificationModal={setShowAddNotificationModal}
        newNotification={newNotification}
        setNewNotification={setNewNotification}
        isSavingNotification={isSavingNotification}
        saveNotification={saveNotification}
        testNotification={testNotification}
        
        showEditNotificationModal={showEditNotificationModal}
        setShowEditNotificationModal={setShowEditNotificationModal}
        editingNotification={editingNotification}
        setEditingNotification={setEditingNotification}
        updateNotification={updateNotification}
        
        // Product modals
        showAddProductModal={showAddProductModal}
        setShowAddProductModal={setShowAddProductModal}
        newProduct={newProduct}
        setNewProduct={setNewProduct}
        saveProduct={saveProduct}
        
        showEditProductModal={showEditProductModal}
        setShowEditProductModal={setShowEditProductModal}
        editingProduct={editingProduct}
        setEditingProduct={setEditingProduct}
        updateProduct={updateProduct}
        
        showDeleteConfirmModal={showDeleteConfirmModal}
        setShowDeleteConfirmModal={setShowDeleteConfirmModal}
        productToDelete={productToDelete}
        setProductToDelete={setProductToDelete}
        confirmDeleteProduct={confirmDeleteProduct}
        
        showCloneModal={showCloneModal}
        setShowCloneModal={setShowCloneModal}
        cloneProduct={cloneProduct}
        setCloneProduct={setCloneProduct}
        confirmCloneProduct={confirmCloneProduct}
        
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        importMode={importMode}
        setImportMode={setImportMode}
        importFile={importFile}
        handleFileSelect={handleFileSelect}
        confirmImportProducts={confirmImportProducts}
        fileInputRef={fileInputRef}
        
        // Bulk notification modal
        showBulkNotificationModal={showBulkNotificationModal}
        setShowBulkNotificationModal={setShowBulkNotificationModal}
        bulkNotificationId={bulkNotificationId}
        setBulkNotificationId={setBulkNotificationId}
        applyBulkNotification={applyBulkNotification}
        selectedCertificatesCount={selectedCertificates.length}
        
        // Group modals
        groups={groups}
        
        // Deployment modal
        selectedDeployment={selectedDeployment}
        setSelectedDeployment={setSelectedDeployment}
        
        // General modals
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        confirmAction={confirmAction}
        
        alertModal={alertModal}
        setAlertModal={setAlertModal}
      />
    </div>
  );
}



export default SSLMonitorDashboard;
